import {
  Box,
  Stack,
  Typography,
  Divider,
  Avatar,
  LinearProgress,
  Chip,
  TextField,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  CircularProgress,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getPerformanceEvaluation, saveMarks } from 'services/evalution.service';
import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';
import { getRubrics, RubricDTO } from 'services/rubric.service';
import { getQuestions } from 'services/questions.service';
import {reassessmentNos} from 'services/assessorDetails.service';
const getPcTypeLabel = (type: any): string => {
  if (!type) return '';
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title : '')).filter(Boolean);
    return titles.join(' / ');
  }
  if (typeof type === 'object' && typeof type.title === 'string') return type.title;
  return String(type);
};

const getPcMainType = (type: any): 'theory' | 'practical' | '' => {
  if (!type) return '';
  if (typeof type === 'string') {
    const t = type.toLowerCase();
    return t === 'theory' || t === 'practical' ? (t as 'theory' | 'practical') : '';
  }
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title.toLowerCase() : '')).filter(Boolean);
    if (titles.some((t) => t === 'task')) return 'practical';
    if (titles.some((t) => t === 'question' || t === 'viva')) return 'theory';
    return '';
  }
  return '';
};

const StudentMarksEntry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [reassessmentData,setReassessmentData] = useState<any>([]);
  const [loadingReNos, setLoadingReNos] = useState(false);
  const { student: initialStudent, batchInfo, assessmentSessionId, studentId, batchId, viewOnly,sessionType } = (location.state as any) || {};
  const resultsReleased = !!batchInfo?.resultsReleased;
  const isReadOnly = Boolean(viewOnly) || resultsReleased;

  const [student, setStudent] = useState<any>(initialStudent || null);
  const [saving, setSaving] = useState(false);
  const [fetchingMarks, setFetchingMarks] = useState(true);
  const [rubrics, setRubrics] = useState<RubricDTO[]>([]);
  const [rubricsLoading, setRubricsLoading] = useState(false);
  const [questionRubricMap, setQuestionRubricMap] = useState<Record<string, string>>({});

  // Fetch existing evaluation and pre-fill marks if already saved
  useEffect(() => {
    const fetchExisting = async () => {
      if (!batchId || !studentId) {
        setFetchingMarks(false);
        return;
      }
      try {
        const res: any = await getPerformanceEvaluation(batchId, studentId, assessmentSessionId);
        const data = res?.data?.data;
        if (data?.evaluation?.length) {
          setStudent((prev: any) => {
            if (!prev) return prev;
            const updated = { ...prev, marksByPc: { ...prev.marksByPc } };
            data.evaluation.forEach((nosEntry: any) => {
              (nosEntry.pcs || []).forEach((pcEntry: any) => {
                const pcId = pcEntry.pcId?._id || pcEntry.pcId;
                if (updated.marksByPc[pcId]) {
                  updated.marksByPc[pcId] = {
                    ...updated.marksByPc[pcId],
                    questions: updated.marksByPc[pcId].questions.map((q: any) => {
                      const match = (pcEntry.questions || []).find((eq: any) => (eq.questionId?._id || eq.questionId) === q.qId);
                      return match ? { ...q, marks: match.obtainedMarks ?? q.marks } : q;
                    })
                  };
                }
              });
            });
            return updated;
          });
        }
      } catch {
        // No existing evaluation — leave marks empty
      } finally {
        setFetchingMarks(false);
      }
    };
    fetchExisting();
  }, [batchId, studentId]);

  // Replace the reassessmentNos useEffect
  useEffect(() => {
    const fetchReNos = async () => {
      if (sessionType !== 'reassessment') return;
      if (!studentId) return; 

      try {
        setLoadingReNos(true);
        const res = await reassessmentNos({
          assessmentSessionId,
          studentId,
          type: sessionType ?? 'reassessment'
        });
        const data = res?.data?.data || [];
        setReassessmentData(data);
      } catch (err) {
        console.error('Failed to load reassessment NOS:', err);
        setReassessmentData([]);
      } finally {
        setLoadingReNos(false);
      }
    };

    fetchReNos();
  }, [sessionType, assessmentSessionId, studentId]);

  // Load rubrics for rubric-based marking (session-scoped so assessor sees rubrics on session questions, not only own rubrics)
  useEffect(() => {
    const fetchRubrics = async () => {
      setRubricsLoading(true);
      try {
        const res = await getRubrics({
          page: pageIndex,
          limit: pageSize,
          ...(batchId && assessmentSessionId
            ? { batchId: String(batchId), assessmentSessionId: String(assessmentSessionId) }
            : {})
        });
        const data = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
        setRubrics(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load rubrics:', error);
        setRubrics([]);
      } finally {
        setRubricsLoading(false);
      }
    };
    fetchRubrics();
  }, [batchId, assessmentSessionId, pageIndex, pageSize]);

  // Load mapping from questionId -> rubricId to ensure each question uses its own rubric
  useEffect(() => {
    const fetchQuestionRubrics = async () => {
      try {
        const res = await getQuestions({
          page: pageIndex,
          limit: pageSize,
          assessmentSessionId: String(assessmentSessionId)
        });
        const root = res?.data;
        const inner = root?.data;
        const list = Array.isArray(inner?.data) ? inner.data : Array.isArray(inner) ? inner : [];
        const map: Record<string, string> = {};
        (list || []).forEach((q: any) => {
          if (q?._id && q?.rubric) {
            map[String(q._id)] = String(q.rubric);
          }
        });
        setQuestionRubricMap(map);
      } catch (error) {
        console.error('Failed to load questions for rubric mapping:', error);
        setQuestionRubricMap({});
      }
    };
    fetchQuestionRubrics();
  }, []);

  if (!student) {
    return (
      <Box p={4}>
        <Typography>No student data found.</Typography>
      </Box>
    );
  }

  if (fetchingMarks) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessionType === 'reassessment' && loadingReNos) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const initials = student.name
    ? student.name
        .split(' ')
        .map((n: string) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'S';

  const qp = batchInfo?.qp || null;

  const nosList: any[] = (() => {
    if (sessionType !== 'reassessment') return batchInfo?.nos || [];

    return reassessmentData.map((nos: any) => ({
      ...nos,
      pcs: (nos.pcs || []).map((pc: any) => ({
        ...pc,

        pcId: pc.pcId?._id ?? pc.pcId
      }))
    }));
  })();

  const resolvePcIdKey = (pc: any): string => String(pc?.pcId?._id ?? pc?.pcId ?? '');

  const pcMaxConfigured = (pc: any, pcData: { maxMarks?: number }) =>
    Number(pc?.totalMarks ?? pc?.pcId?.totalMarks ?? pcData.maxMarks ?? 0) || 0;

  /** Sum obtained / max marks only for PCs under the given NOS list (matches what the UI shows). */
  const sumObtainedForNosList = (list: any[]) =>
    list.reduce((sum: number, nos: any) => {
      const pcSum = (nos.pcs || []).reduce((s: number, pc: any) => {
        const pcId = resolvePcIdKey(pc);
        const pcData =
          (pcId && student.marksByPc?.[pcId]) ||
          (pc?.pcId && student.marksByPc?.[String(pc.pcId)]) ||
          {};
        return s + (pcData.questions || []).reduce((qSum: number, q: any) => qSum + (Number(q.marks) || 0), 0);
      }, 0);
      return sum + pcSum;
    }, 0);

  const sumMaxMarksForNosList = (list: any[]) =>
    list.reduce((sum: number, nos: any) => {
      const nosMax = (nos.pcs || []).reduce((s: number, pc: any) => {
        const pcId = resolvePcIdKey(pc);
        const pcData =
          (pcId && student.marksByPc?.[pcId]) ||
          (pc?.pcId && student.marksByPc?.[String(pc.pcId)]) ||
          {};
        return s + pcMaxConfigured(pc, pcData);
      }, 0);
      return sum + nosMax;
    }, 0);

  // Grand total must match NOS-wise totals shown above (not the full QP template if marksByPc ever held extra PCs).
  const useNosScopedGrandTotals = nosList.length > 0;
  const grandTotal = useNosScopedGrandTotals
    ? sumObtainedForNosList(nosList)
    : Object.values(student.marksByPc).reduce(
        (sum: number, pc: any) => sum + (pc.questions || []).reduce((s: number, q: any) => s + (Number(q.marks) || 0), 0),
        0
      );
  const grandMax = useNosScopedGrandTotals
    ? sumMaxMarksForNosList(nosList)
    : Object.values(student.marksByPc).reduce((sum: number, pc: any) => sum + (Number(pc.maxMarks) || 0), 0);

  // Get rubric id for a given question (from question data or global map)
  const getQuestionRubricId = (q: any): string => {
    if (!q) return '';
    if (q.rubricId || q.rubric) return String(q.rubricId || q.rubric);
    if (q.qId && questionRubricMap[q.qId]) return questionRubricMap[q.qId];
    return '';
  };

  // Derive currently selected rubric option value for a question based on its marks and its configured rubric.
  // Here level.scorePercent is treated as the absolute marks for that level.
  const getSelectedRubricValue = (q: any): string => {
    if (!rubrics.length || q == null) return '';
    const rubricId = getQuestionRubricId(q);
    if (!rubricId) return '';

    const obtained = Number(q.marks);
    if (!Number.isFinite(obtained)) return '';

    const rubric = rubrics.find((r) => ((r as any)._id || '') === String(rubricId));
    if (rubric && Array.isArray(rubric.levels)) {
      for (let idx = 0; idx < rubric.levels.length; idx += 1) {
        const level = rubric.levels[idx];
        const levelMarks = Number(level.scorePercent) || 0;
        if (levelMarks === obtained) {
          return `${(rubric as any)._id || ''}:${idx}`;
        }
      }
    }
    return '';
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Page Header */}
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">{isReadOnly ? 'View Rubrics' : 'Enter Rubrics'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {batchInfo?.batchName || ''}
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pr: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: 15, fontWeight: 700 }}>{initials}</Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {student.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {student.email}
              </Typography>
            </Box>
          </Stack>
        }
      />

      {isReadOnly && <Alert severity="info">You can view marks but editing is disabled.</Alert>}
      {/* Assessment Info Banner */}
      <MainCard content={false}>
        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Course */}
            {batchInfo?.courseName && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Course
                </Typography>
                <Box mt={0.25}>
                  <Chip label={batchInfo.courseName} size="small" color="secondary" />
                </Box>
              </Grid>
            )}

            {/* Template */}
            {batchInfo?.templateName && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Template
                </Typography>
                <Box mt={0.25}>
                  <Chip label={batchInfo.templateName} size="small" color="secondary" />
                </Box>
              </Grid>
            )}

            {/* QP */}
            {qp && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Qualification Pack
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} mt={0.25} flexWrap="wrap" useFlexGap>
                  <Chip label={qp.qpCode} size="small" color="secondary" />
                  <Typography variant="body2" fontWeight={600}>
                    {qp.qpName}
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      </MainCard>

      {/* NOS → PC → Questions structure */}
      {nosList.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
          {nosList.map((nos: any, nosIdx: number) => {
            // NOS-level totals: obtained from entered marks, max from configured PC totals (same keys / fallbacks as PC rows)
            const nosTotal = (nos.pcs || []).reduce((sum: number, pc: any) => {
              const pcId = resolvePcIdKey(pc);
              const pcData =
                (pcId && student.marksByPc?.[pcId]) ||
                (pc?.pcId && student.marksByPc?.[String(pc.pcId)]) ||
                {};
              return sum + (pcData.questions || []).reduce((s: number, q: any) => s + (Number(q.marks) || 0), 0);
            }, 0);
            const nosMax = (nos.pcs || []).reduce((sum: number, pc: any) => {
              const pcId = resolvePcIdKey(pc);
              const pcData =
                (pcId && student.marksByPc?.[pcId]) ||
                (pc?.pcId && student.marksByPc?.[String(pc.pcId)]) ||
                {};
              return sum + pcMaxConfigured(pc, pcData);
            }, 0);
            const nosPercent = nosMax > 0 ? Math.round((nosTotal / nosMax) * 100) : 0;

            return (
              <Accordion
                key={String(nos.nosId ?? nos._id ?? nosIdx)}
                defaultExpanded={nosIdx === 0}
                sx={{
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '0 !important',
                  '&:first-of-type': { borderTopLeftRadius: '8px !important', borderTopRightRadius: '8px !important' },
                  '&:last-of-type': { borderBottomLeftRadius: '8px !important', borderBottomRightRadius: '8px !important' },
                  '& + .MuiAccordion-root': { borderTop: 0 }
                }}
              >
                <AccordionSummary
                  sx={{
                    bgcolor: 'primary.lighter',
                    px: 2,
                    minHeight: 56,
                    '&.Mui-expanded': { minHeight: 56 }
                  }}
                >
                  <Box sx={{ width: '100%', pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={700}>
                          NOS:{' '}
                        </Typography>
                        <Chip label={nos.nosCode} size="small" color="primary" sx={{ fontWeight: 700 }} />
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {nos.nosName}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Total Marks: {nosMax}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 180, textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        {nosTotal} / {nosMax}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(nosPercent, 100)}
                        color={nosPercent >= 80 ? 'success' : nosPercent >= 50 ? 'warning' : 'primary'}
                        sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, bgcolor: 'background.paper' }}>
                  <Divider />
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(nos.pcs || []).map((pc: any, pcIdx: number) => {
                      const pcId = pc?.pcId?._id || pc?.pcId;
                      const pcIsCritical = Boolean(pc?.isCritical ?? pc?.pcId?.isCritical);
                      const pcData = student.marksByPc?.[pcId] || {};
                      const questions = pcData.questions || [];
                      const pcTotal = questions.reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
                      // Max marks per PC should follow the configured PC total
                      const pcMax = pc?.totalMarks ?? pc?.pcId?.totalMarks ?? pcData.maxMarks ?? 0;
                      const pcPercent = pcMax > 0 ? Math.round((pcTotal / pcMax) * 100) : 0;
                      const pcMainType = getPcMainType(pc?.type ?? pc?.pcId?.type);
                      const pcTypeLabel = getPcTypeLabel(pc?.type ?? pc?.pcId?.type) || '-';

                      return (
                        <Accordion
                          key={pcId}
                          defaultExpanded={pcIdx === 0}
                          sx={{
                            '&:before': { display: 'none' },
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            '& .MuiAccordionSummary-root': { bgcolor: 'grey.50' },
                            '& .MuiAccordionDetails-root': { p: 1.5 }
                          }}
                        >
                          <AccordionSummary sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                            <Box sx={{ width: '100%', pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {(pc?.pcCode ?? pc?.pcId?.pcCode) || '-'} — {(pc?.pcName ?? pc?.pcId?.pcName) || '-'}
                                </Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontWeight: 500, fontSize: '0.78rem' }}
                                  label={
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography
                                        component="span"
                                        sx={{ textTransform: 'capitalize', fontWeight: 500, fontSize: '0.78rem' }}
                                      >
                                        {pcTypeLabel}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: pcIsCritical ? 'error.main' : 'text.secondary',
                                          fontWeight: 500,
                                          ml: 0.5,
                                          textTransform: 'none',
                                          fontSize: '0.78rem'
                                        }}
                                      >
                                        - {pcIsCritical ? 'Critical' : 'Non-critical'}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </Stack>
                              <Box sx={{ width: 160, textAlign: 'right', flexShrink: 0 }}>
                                <Typography variant="caption" fontWeight={700}>
                                  {pcTotal} / {pcMax}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(pcPercent, 100)}
                                  color={pcPercent >= 80 ? 'success' : pcPercent >= 50 ? 'warning' : 'primary'}
                                  sx={{ height: 5, borderRadius: 1, mt: 0.25 }}
                                />
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {questions.length > 0 ? (
                                questions.map((q: any, qIdx: number) => (
                                  <Box
                                    key={q.qId || qIdx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      p: 1.25,
                                      borderRadius: 1,
                                      bgcolor: 'background.paper',
                                      border: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Chip label={`Q${qIdx + 1}`} size="small" color="secondary" sx={{ fontWeight: 700, minWidth: 36 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {q.questionText || q.question || `Question ${qIdx + 1}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Max: {q.maxMarks} mark{q.maxMarks > 1 ? 's' : ''} | Obtained: {Number(q.marks) || 0}
                                      </Typography>
                                    </Box>
                                    <TextField
                                      select
                                      size="small"
                                      value={getSelectedRubricValue(q)}
                                      sx={{ minWidth: 200 }}
                                      SelectProps={{ displayEmpty: true }}
                                      disabled={isReadOnly}
                                      onChange={(e) => {
                                        if (isReadOnly) return;
                                        const value = e.target.value as string;
                                        const updated = { ...student };
                                        let marks = 0;

                                        const rubricId = getQuestionRubricId(q);
                                        if (value && rubricId) {
                                          const [, levelIndexStr] = value.split(':');
                                          const rubric = rubrics.find((r) => ((r as any)._id || '') === String(rubricId));
                                          const levelIndex = Number(levelIndexStr);
                                          const level = rubric?.levels?.[levelIndex];
                                          if (rubric && level) {
                                            const levelMarks = Number(level.scorePercent) || 0;
                                            marks = levelMarks;
                                          }
                                        }

                                        if (updated.marksByPc?.[pcId]?.questions?.[qIdx]) {
                                          updated.marksByPc[pcId].questions[qIdx].marks = marks;
                                          setStudent({ ...updated });
                                        }
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>{rubricsLoading ? 'Loading rubrics…' : 'Select rubric'}</em>
                                      </MenuItem>
                                      {!rubricsLoading && (!rubrics.length || !getQuestionRubricId(q)) && (
                                        <MenuItem disabled value="_no_rubrics">
                                          No rubric configured for this question
                                        </MenuItem>
                                      )}
                                      {rubrics
                                        .filter((rubric) => ((rubric as any)._id || '') === getQuestionRubricId(q))
                                        .flatMap((rubric) =>
                                          rubric.levels.map((level, idx) => {
                                            const key = `${(rubric as any)._id || ''}:${idx}`;
                                            const levelMarks = Number(level.scorePercent) || 0;
                                            return (
                                              <MenuItem key={key} value={key}>
                                                {`${level.levelName} (${levelMarks} mark${levelMarks === 1 ? '' : 's'})`}
                                              </MenuItem>
                                            );
                                          })
                                        )}
                                    </TextField>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                  No questions available for this PC.
                                </Typography>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        // Fallback: flat PC list if no NOS structure
        <Box display="flex" flexDirection="column" gap={2}>
          {Object.entries(student.marksByPc).map(([pcId, pc]: any) => {
            const total = pc.questions.reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
            const max = pc.maxMarks ?? 0;
            const percent = max > 0 ? Math.round((total / max) * 100) : 0;
            return (
              <MainCard key={pcId} content={false}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {pc.pcCode ? `${pc.pcCode} — ${pc.pcName}` : pc.pcName}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {total} / {max}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={Math.min(percent, 100)} sx={{ height: 6, borderRadius: 1, mb: 1.5 }} />
                  <Divider sx={{ mb: 1.5 }} />
                  {pc.questions.map((q: any, i: number) => (
                    <Box key={q.qId || i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Chip label={`Q${i + 1}`} size="small" color="secondary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {q.questionText}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Max: {q.maxMarks} | Obtained: {Number(q.marks) || 0}
                        </Typography>
                      </Box>
                      <TextField
                        select
                        size="small"
                        label="Rubric"
                        value={getSelectedRubricValue(q)}
                        sx={{ minWidth: 200 }}
                        SelectProps={{ displayEmpty: true }}
                        disabled={resultsReleased}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          const value = e.target.value as string;
                          const updated = { ...student };
                          let marks = 0;

                          const rubricId = getQuestionRubricId(q);
                          if (value && rubricId) {
                            const [, levelIndexStr] = value.split(':');
                            const rubric = rubrics.find((r) => ((r as any)._id || '') === String(rubricId));
                            const levelIndex = Number(levelIndexStr);
                            const level = rubric?.levels?.[levelIndex];
                            if (rubric && level) {
                              const levelMarks = Number(level.scorePercent) || 0;
                              marks = levelMarks;
                            }
                          }

                          if (updated.marksByPc?.[pcId]?.questions?.[i]) {
                            updated.marksByPc[pcId].questions[i].marks = marks;
                            setStudent({ ...updated });
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>{rubricsLoading ? 'Loading rubrics…' : 'Select rubric'}</em>
                        </MenuItem>
                        {!rubricsLoading && (!rubrics.length || !getQuestionRubricId(q)) && (
                          <MenuItem disabled value="_no_rubrics">
                            No rubric configured for this question
                          </MenuItem>
                        )}
                        {rubrics
                          .filter((rubric) => ((rubric as any)._id || '') === getQuestionRubricId(q))
                          .flatMap((rubric) =>
                            rubric.levels.map((level, idx) => {
                              const key = `${(rubric as any)._id || ''}:${idx}`;
                              const levelMarks = Number(level.scorePercent) || 0;
                              return (
                                <MenuItem key={key} value={key}>
                                  {(rubric.name || 'Rubric') +
                                    ' - ' +
                                    level.levelName +
                                    ` (${levelMarks} mark${levelMarks === 1 ? '' : 's'})`}
                                </MenuItem>
                              );
                            })
                          )}
                      </TextField>
                    </Box>
                  ))}
                </Box>
              </MainCard>
            );
          })}
        </Box>
      )}

      {/* Footer: Grand Total + Save */}
      <MainCard content={false}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Grand Total
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {grandTotal} / {grandMax}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" disabled={saving} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={async () => {
                  const questionCount = nosList.reduce(
                    (acc: number, nos: any) =>
                      acc +
                      (nos.pcs || []).reduce((pcAcc: number, pc: any) => {
                        const pcId = pc.pcId?._id ?? pc.pcId;
                        const key = pcId != null ? String(pcId) : '';
                        const pcData =
                          (key && student.marksByPc?.[key]) ||
                          (pc?.pcId && student.marksByPc?.[String(pc.pcId)]) ||
                          {};
                        return pcAcc + (pcData.questions || []).length;
                      }, 0),
                    0
                  );
                  if (questionCount === 0) {
                    Snackbar(
                      'There are no questions to mark. Add questions to this assessment before saving marks.',
                      'error'
                    );
                    return;
                  }

                  setSaving(true);
                  try {
                    // Build evaluation array: NOS → PCs → questions with entered marks
                    // Fix the save payload — for reassessment, nosId comes from nos.nosId not nos._id
                    const evaluation = nosList.map((nos: any) => ({
                      nosId: nos.nosId ?? nos._id, // ← reassessment uses nosId, initial uses _id
                      pcs: (nos.pcs || []).map((pc: any) => {
                        const pcId = pc.pcId?._id ?? pc.pcId;
                        const pcData = student.marksByPc?.[pcId] || {};
                        return {
                          pcId,
                          questions: (pcData.questions || []).map((q: any) => ({
                            questionId: q.qId,
                            obtainedMarks: Number(q.marks) || 0
                          }))
                        };
                      })
                    }));

                    const payload = {
                      qpId: batchInfo?.qp?.qpId,
                      batchId: batchInfo?.batchId,
                      assessmentSessionId,
                      studentId: student.id,
                      evaluation,
                      status: 'submitted'
                    };

                    await saveMarks(payload);
                    Snackbar('Marks saved successfully!', 'success');
                    setTimeout(() => navigate(-1), 1200);
                  } catch (err: any) {
                    const msg = err?.response?.data?.message || err?.message || 'Failed to save marks. Please try again.';
                    Snackbar(msg, 'error');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Marks'}
              </Button>
            )}
          </Stack>
        </Box>
      </MainCard>
    </Box>
  );
};

export default StudentMarksEntry;
